/**
 * LayoffRadarButton (#5 Layoff/reorg-radar) — knop + mini-dialoog op de
 * Signal Hunting pagina. Perplexity zoekt recente ontslagrondes en maakt
 * "layoff"-signalen aan: sales-kans (vervanging/consolidatie) én talent-kans
 * (vrijkomende profielen in de metadata).
 */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { Radar, Loader2, X } from "lucide-react";

export default function LayoffRadarButton({ onDone }) {
    const [open, setOpen] = useState(false);
    const [region, setRegion] = useState("België");
    const [sector, setSector] = useState("");
    const [running, setRunning] = useState(false);

    const run = async () => {
        setRunning(true);
        try {
            const r = await api.post("/radar/layoffs/run", {
                region: region.trim() || "België",
                sector: sector.trim() || null,
                limit: 5,
            });
            const n = (r.data.created || []).length;
            toast.success(n > 0
                ? i18n.t("layoffradar.run_ok", { n })
                : i18n.t("layoffradar.run_none", { skipped: r.data.skipped }));
            setOpen(false);
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("layoffradar.run_failed")); }
        finally { setRunning(false); }
    };

    return (
        <>
            <button onClick={() => setOpen(true)} data-testid="layoff-radar-open"
                title={i18n.t("layoffradar.tip")}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-[#f87171]/50 text-[#f87171] text-xs font-medium hover:bg-[#f87171]/10">
                <Radar size={13} /> {i18n.t("layoffradar.btn")}
            </button>
            {open && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4"
                    data-testid="layoff-radar-modal" onClick={running ? undefined : () => setOpen(false)}>
                    <div className="w-full max-w-md rounded-xl border border-white/10 bg-[var(--jr-surface)] p-6 max-h-[calc(100dvh-2rem)] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between mb-3">
                            <h3 className="text-base font-semibold text-white flex items-center gap-2">
                                <Radar size={16} className="text-[#f87171]" /> {i18n.t("layoffradar.title")}
                            </h3>
                            <button onClick={() => setOpen(false)} data-testid="layoff-radar-close"
                                className="p-1 rounded-full text-[var(--jr-text-3)] hover:text-white"><X size={15} /></button>
                        </div>
                        <p className="text-xs text-[var(--jr-text-2)] mb-4">{i18n.t("layoffradar.explain")}</p>
                        <label className="block text-xs text-[var(--jr-text-2)] mb-1">{i18n.t("layoffradar.region_lbl")}</label>
                        <input value={region} onChange={(e) => setRegion(e.target.value)} data-testid="layoff-radar-region"
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded px-2.5 py-2 text-sm text-white outline-none focus:border-[#f87171] mb-3" />
                        <label className="block text-xs text-[var(--jr-text-2)] mb-1">{i18n.t("layoffradar.sector_lbl")}</label>
                        <input value={sector} onChange={(e) => setSector(e.target.value)} data-testid="layoff-radar-sector"
                            placeholder={i18n.t("layoffradar.sector_placeholder")}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded px-2.5 py-2 text-sm text-white outline-none focus:border-[#f87171] mb-4" />
                        <button onClick={run} disabled={running} data-testid="layoff-radar-run"
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#f87171] text-black text-sm font-semibold hover:bg-[#f87171]/85 disabled:opacity-50">
                            {running ? <Loader2 size={14} className="animate-spin" /> : <Radar size={14} />}
                            {running ? i18n.t("layoffradar.running") : i18n.t("layoffradar.run_btn")}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
